// src/pages/program/OptionalOverviewPage.jsx

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getOptionalExercises } from '../../data/optionalExercises';
import { 
  Dumbbell, 
  Heart,
  Play,
  Clock,
  CheckCircle,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui';

const OPTIONAL_STORAGE_KEY = 'acl_care_optional_progress';

export function OptionalOverviewPage() {
  const { phaseId } = useParams();
  const navigate = useNavigate();
  const { aclCare } = useAuth();

  const [showResumeModal, setShowResumeModal] = useState(false);
  const [existingSession, setExistingSession] = useState(null);

  const phaseNum = parseInt(phaseId);
  const phaseKey = `phase_${phaseNum}`;
  const userPhaseData = aclCare?.phases?.[phaseKey];
  const optionalCompletions = userPhaseData?.completions?.optional || 0;

  // Get exercises for this phase
  const exercises = getOptionalExercises(phaseNum);

  // Check for existing session on mount
  useEffect(() => {
    const stored = localStorage.getItem(OPTIONAL_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.phaseNum === phaseNum) {
          setExistingSession(parsed);
          setShowResumeModal(true);
        }
      } catch (e) {
        console.error('Error parsing stored optional session:', e);
      }
    }
  }, [phaseNum]);

  const handleStartSession = () => {
    // Initialize new session in localStorage
    const newSession = {
      date: new Date().toDateString(),
      phaseNum,
      phase: 'warmup',
      warmupCompleted: false,
      currentExerciseIndex: 0,
      exerciseData: {},
      completed: false,
      startedAt: new Date().toISOString(),
    };

    localStorage.setItem(OPTIONAL_STORAGE_KEY, JSON.stringify(newSession));
    navigate(`/program/phase/${phaseId}/optional/play`);
  };

  const handleContinueSession = () => {
    setShowResumeModal(false);
    navigate(`/program/phase/${phaseId}/optional/play`);
  };

  const handleStartNew = () => {
    localStorage.removeItem(OPTIONAL_STORAGE_KEY);
    setExistingSession(null);
    setShowResumeModal(false);
  };

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center mb-6">
        <Dumbbell className="h-6 w-6 text-primary mr-2" />
        <h1 className="text-2xl font-bold text-gray-900">
          Phase {phaseNum} - Optional Session
        </h1>
      </div>

      {/* Completion Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
            <span className="text-gray-700">Completed</span>
          </div>
          <span className="text-xl font-bold text-gray-900">
            {optionalCompletions}x
          </span>
        </div>
      </div>

      {/* Warmup Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex items-center mb-3">
          <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
            <Heart className="h-5 w-5 text-red-500" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Warmup</h2>
            <p className="text-sm text-gray-500">10-minute cardio</p>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          Complete 10 minutes of cardio on your equipment of choice 
          (e.g., treadmill, elliptical, stationary bike, rowing ergometer).
        </p>
      </div>

      {/* Exercises Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Exercises</h2>
            <span className="text-sm text-gray-500">{exercises.length} exercises</span>
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {exercises.map((exercise, index) => (
            <div key={exercise.id} className="p-4 flex items-center">
              <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center mr-3 flex-shrink-0">
                <span className="text-sm font-medium text-gray-600">{index + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{exercise.exercise}</p>
                <p className="text-sm text-gray-500">{exercise.documentation}</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-300 flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>

      {/* Estimated Time */}
      <div className="flex items-center justify-center text-sm text-gray-500 mb-6">
        <Clock className="h-4 w-4 mr-1" />
        <span>Estimated time: 30-45 minutes</span>
      </div>

      {/* Start Button */}
      <Button
        onClick={handleStartSession}
        fullWidth
        size="lg"
      >
        <Play className="h-5 w-5 mr-2" />
        Start Optional Session
      </Button>

      {/* Resume Session Modal */}
      {showResumeModal && existingSession && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" />
          
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Session In Progress
              </h2>
              <p className="text-gray-600 mb-6">
                You have an unfinished optional session. What would you like to do?
              </p>

              <div className="space-y-3">
                <Button
                  onClick={handleContinueSession}
                  fullWidth
                >
                  Continue Where I Left Off
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleStartNew}
                  fullWidth
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Start New Session
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}